import { createDepartmentTreeList } from 'util/createDepartmentTreeList';
import { selectAdminApiUrl } from 'util/selector/apiSelector';
import { createResourceContentSelector } from 'util/selector/resourceSelector';

import { Department, DepartmentModel } from 'api/dto/Department.dto';
import { Resource } from 'model/Resource';
import { DepartmentState } from 'reduxStore/department/initialState';
import { selectCurrentProjectOnlyId } from 'reduxStore/project/projectSelector';
import { createSelector } from '@reduxjs/toolkit';
import { AdminState } from 'reduxStore/appStore';

export function selectDepartmentDomain(state: AdminState): DepartmentState.Domain {
  return state.department as DepartmentState.Domain;
}

export const selectDepartmentListResource = createSelector(
  selectDepartmentDomain,
  (domain) =>
    (domain && domain.listResource) || Resource.createPendingResource<DepartmentModel[]>(),
);

export const selectDepartmentFlatListResource = createSelector(
  selectDepartmentListResource,
  (listResource) => {
    const departments = listResource.data || [];
    const flatDepartments = departments.reduce<DepartmentModel[]>((nested, department) => {
      nested.push(department);
      const children = Department.getNestedChildren(department);
      return nested.concat(children);
    }, []);
    return Resource.createResolvedResource({ data: flatDepartments });
  },
);

export const selectDepartmentList = createResourceContentSelector(selectDepartmentListResource, []);
export const selectDepartmentFlatList = createResourceContentSelector(
  selectDepartmentFlatListResource,
);

export const selectDepartmentTreeList = createSelector(selectDepartmentList, (departments) =>
  departments ? createDepartmentTreeList(departments) : [],
);

export const selectDepartmentDryListResource = createSelector(
  selectDepartmentDomain,
  (domain) =>
    (domain && domain.dryListResource) || Resource.createPendingResource<DepartmentModel[]>(),
);

export const selectIsDryUpload = createSelector(
  selectDepartmentDryListResource,
  (dryListResource) =>
    dryListResource.loadingState === 'resolved' &&
    dryListResource.data != null &&
    dryListResource.data.length > 0,
);

export const selectDepartmentUploadedListResource = createSelector(
  selectDepartmentListResource,
  selectDepartmentDryListResource,
  (listResource, dryListResource) =>
    dryListResource.loadingState === 'resolved' ? dryListResource : listResource,
);

export const selectExportToExcelUrl = createSelector(
  selectAdminApiUrl,
  selectCurrentProjectOnlyId,
  (adminApiUrl, projectId) => `${adminApiUrl}/api/v1/project/${projectId}/department/export`,
);

export const selectHasDepartment = createSelector(
  selectDepartmentList,
  (departmentList) => departmentList && departmentList.length > 0,
);

export const selectHasDepartmentDryList = createSelector(
  selectDepartmentDryListResource,
  (dryListResource) => dryListResource.loadingState === 'resolved',
);

export const selectIsDepartmentListUploaded = createSelector(
  selectDepartmentListResource,
  (departmentResource) =>
    departmentResource && departmentResource.data && departmentResource.data.length > 0,
);
